import React from 'react';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { useQuery, useApolloClient } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';

import { openAuthURL, getIntegrationToken, generateIntegrationOAuthUrl } from 'helpers';

import { Toast, Modal, TokenIntegrationModalContent } from 'components';
import { useTranslation } from 'hooks';

import styles from './AuthLink.module.scss';

const IntegrationLinkSettingsQuery = gql`
  query integrationLinkSettings($integrationType: String!) {
    me {
      userProfile {
        id
      }
      organization {
        id
      }
    }
    integrations(integrationType: $integrationType) {
      type
      title
      setupPath
      image
      imageIcon
      imageProviderIcon
      userConfig {
        instructions
        fields {
          name
          label
        }
      }
      features {
        configurable
      }
    }
  }
`;

interface AuthLinkData {
  integrationSettings: {
    enabled: boolean;
  };
  me: {
    userProfile: User;
    organization: Organization;
    integrations: UserIntegration[];
  };
  integrations: Integration[];
}

interface AuthLinkProps {
  integrationType: string;
  nodeName: string;
}

export const AuthLink: React.FC<AuthLinkProps> = ({ integrationType, nodeName }) => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const [modalIsOpen, setIsOpen] = React.useState(false);
  const [integrationToConfigure, setIntegrationToConfigure] = React.useState({});

  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const { error, data } = useQuery<AuthLinkData>(IntegrationLinkSettingsQuery, {
    fetchPolicy: 'cache-and-network',
    variables: { integrationType },
  });

  if (data === undefined) return <div className={classNames(styles.title)}>{nodeName}</div>;

  if (error) return <div className={classNames(styles.title)}>{nodeName}</div>;

  const userId = data.me?.userProfile?.id || '';
  const organizationId = data.me?.organization?.id || '';
  const integration = data.integrations[0];

  const integrationId = integration.type!.slice(0, integration.type!.indexOf('@'));

  // If token integration show modal, otherwise generate OAuth url
  const configureIntegrations = () => {
    if (integration.userConfig) {
      setIntegrationToConfigure(integration);
      openModal();
    } else {
      getIntegrationToken().then(
        (token: any) => {
          const url = generateIntegrationOAuthUrl(
            token,
            integration.setupPath!,
            integrationId,
            userId,
            organizationId
          );
          openAuthURL(url, true, '_blank', () => {
            client.reFetchObservableQueries();
          });
        },
        () => {
          toast(<Toast message={t('toasts.pop-up-blocked')} hasCloseButton />, {
            position: toast.POSITION.BOTTOM_LEFT,
            hideProgressBar: true,
            closeButton: false,
            closeOnClick: false,
            autoClose: false,
          });
        }
      );
    }
  };

  return (
    <>
      <div className={classNames(styles.title, styles.clickable)} onClick={configureIntegrations}>
        {nodeName}
      </div>
      <Modal isOpen={modalIsOpen} onRequestClose={closeModal}>
        <TokenIntegrationModalContent
          integration={integrationToConfigure}
          closeModal={closeModal}
        />
      </Modal>
    </>
  );
};
